import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Vote, CheckCircle2, User, Shield, AlertTriangle, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VotingPage = () => {
  const { electionId } = useParams();
  const navigate = useNavigate();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('verify'); // verify, vote, success
  const [hasVoted, setHasVoted] = useState(false);
  
  // Voter credentials
  const [credentials, setCredentials] = useState({
    member_id: '',
    email: ''
  });
  
  // Selected votes
  const [selectedVotes, setSelectedVotes] = useState({});

  useEffect(() => {
    fetchElection();
  }, [electionId]);

  const fetchElection = async () => {
    try {
      const response = await axios.get(`${API}/elections/${electionId}/active`);
      setElection(response.data);
    } catch (error) {
      console.error('Error fetching election:', error);
      if (error.response?.status === 400) {
        toast.error('This election is not currently active');
      } else {
        toast.error('Election not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Check if already voted
      const checkResponse = await axios.get(`${API}/elections/${electionId}/check-voted?email=${credentials.email}`);
      if (checkResponse.data.has_voted) {
        setHasVoted(true);
        toast.error('You have already voted in this election');
      } else {
        setStep('vote');
        toast.success('Verification successful! You may now cast your vote.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Verification failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVoteSelect = (position, candidateId) => {
    setSelectedVotes(prev => ({
      ...prev,
      [position]: candidateId
    }));
  };

  const handleSubmitVote = async () => {
    // Verify all positions have been voted
    const unvotedPositions = (election?.positions || []).filter(pos => !selectedVotes[pos]);
    if (unvotedPositions.length > 0) {
      toast.error(`Please vote for all positions: ${unvotedPositions.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/elections/${electionId}/vote`, {
        election_id: electionId,
        member_id: credentials.member_id,
        email: credentials.email,
        votes: selectedVotes
      });
      
      setStep('success');
      toast.success('Your vote has been recorded successfully!');
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit vote');
    } finally {
      setSubmitting(false);
    }
  };

  const getCandidatesByPosition = (position) => {
    return (election?.candidates || []).filter(c => c.position_applying === position);
  };

  const getRegionColor = (region) => {
    const colors = {
      'New Guinea Islands': 'bg-blue-500',
      'Momase': 'bg-green-500',
      'Highlands': 'bg-orange-500',
      'Southern': 'bg-purple-500',
      'National Capital District': 'bg-red-500'
    };
    return colors[region] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!election) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle className="mx-auto text-orange-500 mb-4" size={64} />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Election Not Available</h1>
          <p className="text-gray-600 mb-6">This election is either not active or does not exist.</p>
          <Button onClick={() => navigate('/events')} className="bg-orange-500 hover:bg-orange-600">
            Back to Events
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-br from-orange-500 to-orange-600 py-10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Vote className="mx-auto text-white mb-4" size={48} />
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{election.title}</h1>
          <p className="text-orange-100">{election.description}</p>
          <div className="mt-4 flex justify-center gap-4 text-sm text-white/80">
            <span>Start: {new Date(election.start_date).toLocaleDateString()}</span>
            <span>•</span>
            <span>End: {new Date(election.end_date).toLocaleDateString()}</span>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Step: Verify Identity */}
        {step === 'verify' && !hasVoted && (
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="text-center mb-8">
              <Shield className="mx-auto text-orange-500 mb-4" size={48} />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Voter Verification</h2>
              <p className="text-gray-600">Please enter your membership details to verify your eligibility to vote</p>
            </div>

            <form onSubmit={handleVerify} className="max-w-md mx-auto space-y-6">
              <div>
                <Label htmlFor="member_id">Member ID / Employee Number</Label>
                <Input
                  id="member_id"
                  data-testid="member-id-input"
                  value={credentials.member_id}
                  onChange={(e) => setCredentials(prev => ({ ...prev, member_id: e.target.value }))}
                  required
                  placeholder="Enter your member ID"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Registered Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={credentials.email}
                  onChange={(e) => setCredentials(prev => ({ ...prev, email: e.target.value }))}
                  required
                  placeholder="Enter your email"
                  className="mt-1"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Only active PEA PNG members can vote. Your vote is confidential and secure.
                </p>
              </div>

              <Button type="submit" disabled={loading} data-testid="verify-btn" className="w-full bg-orange-500 hover:bg-orange-600">
                {loading ? 'Verifying...' : 'Verify & Proceed to Vote'}
              </Button>
            </form>
          </div>
        )}

        {/* Already Voted */}
        {hasVoted && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <CheckCircle2 className="mx-auto text-green-500 mb-4" size={64} />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Already Voted</h2>
            <p className="text-gray-600 mb-6">You have already submitted your vote for this election. Thank you for participating!</p>
            <Button onClick={() => navigate('/events')} variant="outline">
              Back to Events
            </Button>
          </div>
        )}

        {/* Step: Vote */}
        {step === 'vote' && (
          <div className="space-y-8">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
              <p className="text-blue-800">
                <strong>Voting as:</strong> {credentials.email}
              </p>
            </div>

            {/* Executive Positions */}
            {['President', 'Deputy President', 'Vice Deputy President'].map(position => {
              const candidates = getCandidatesByPosition(position);
              if (candidates.length === 0) return null;
              
              return (
                <div key={position} className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="text-orange-500" size={24} />
                    {position}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {candidates.map(candidate => (
                      <label
                        key={candidate.id}
                        className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedVotes[position] === candidate.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name={position}
                          value={candidate.id}
                          checked={selectedVotes[position] === candidate.id}
                          onChange={() => handleVoteSelect(position, candidate.id)}
                          className="sr-only"
                        />
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                          {candidate.photo_url ? (
                            <img src={candidate.photo_url} alt={candidate.name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <span className="text-white font-bold text-xl">{candidate.name.charAt(0)}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">{candidate.name}</p>
                          {candidate.bio && <p className="text-sm text-gray-600 mt-1">{candidate.bio}</p>}
                        </div>
                        {selectedVotes[position] === candidate.id && (
                          <CheckCircle2 className="absolute top-2 right-2 text-orange-500" size={24} />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Ordinary Members by Region */}
            {['New Guinea Islands', 'Momase', 'Highlands', 'Southern', 'National Capital District'].map(region => {
              const positionName = `Ordinary Member - ${region}`;
              const candidates = getCandidatesByPosition(positionName);
              if (candidates.length === 0) return null;
              
              const regionSeats = region === 'National Capital District' ? 4 : 2;
              
              return (
                <div key={region} className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Users className="text-blue-500" size={24} />
                    Ordinary Members - {region}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">{regionSeats} positions available</p>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    {candidates.map(candidate => (
                      <label
                        key={candidate.id}
                        className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedVotes[positionName] === candidate.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name={positionName}
                          value={candidate.id}
                          checked={selectedVotes[positionName] === candidate.id}
                          onChange={() => handleVoteSelect(positionName, candidate.id)}
                          className="sr-only"
                        />
                        <div className={`w-12 h-12 ${getRegionColor(region)} rounded-full flex items-center justify-center flex-shrink-0`}>
                          {candidate.photo_url ? (
                            <img src={candidate.photo_url} alt={candidate.name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <span className="text-white font-bold">{candidate.name.charAt(0)}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">{candidate.name}</p>
                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs text-white rounded ${getRegionColor(region)}`}>
                            {region}
                          </span>
                        </div>
                        {selectedVotes[positionName] === candidate.id && (
                          <CheckCircle2 className="absolute top-2 right-2 text-blue-500" size={20} />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Submit Button */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="mb-4">
                <h3 className="font-bold text-gray-900 mb-2">Vote Summary</h3>
                <p className="text-sm text-gray-600">
                  You have selected {Object.keys(selectedVotes).length} of {election.positions?.length || 0} positions.
                </p>
              </div>
              
              <Button
                onClick={handleSubmitVote}
                disabled={submitting || Object.keys(selectedVotes).length !== (election.positions?.length || 0)}
                className="w-full bg-green-500 hover:bg-green-600 text-lg py-6"
              >
                {submitting ? 'Submitting Vote...' : 'Submit My Vote'}
              </Button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                Your vote is confidential and cannot be changed once submitted.
              </p>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="text-green-500" size={48} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Vote Submitted Successfully!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for participating in the PEA PNG National Executive Committee Election. 
              Your vote has been recorded securely.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                Results will be announced after the election closes on {new Date(election.end_date).toLocaleDateString()}.
              </p>
            </div>
            <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600">
              Return to Home
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default VotingPage;
